import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ScanFace } from "lucide-react";
import { Loader } from "@/components/common/Loader";
import { EmptyState, StatusBadge } from "@/components/dashboard/ui";
import { PhotoMasonry } from "@/components/gallery/PhotoMasonry";
import { Lightbox } from "@/components/gallery/Lightbox";
import { ClientNav, SubmitBar } from "@/components/client/ClientChrome";
import { SubmitConfirm, SubmitSuccess } from "@/components/client/SubmitFlow";
import { LeadModal } from "@/components/client/LeadModal";
import { useClientGallery } from "@/lib/useClientGallery";

export default function ClientSelected() {
  const { eventId } = useParams();
  const g = useClientGallery(eventId);
  const [idx, setIdx] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [success, setSuccess] = useState(0);
  const [leadPhoto, setLeadPhoto] = useState(null);

  if (g.loading) return <div className="min-h-screen bg-cream"><Loader full label="Loading your selection" /></div>;
  if (g.error || !g.event) return <div className="min-h-screen flex items-center justify-center text-ink/60 bg-cream">Gallery not found.</div>;

  const selected = g.photos.filter((p) => p.selected);
  const doSubmit = async (comments) => {
    try { const r = await g.submit(comments); setConfirm(false); setSuccess(r.count); } catch { toast.error("Could not submit."); }
  };

  return (
    <div className="min-h-screen bg-cream pb-28">
      <ClientNav eventId={eventId} eventName={g.event.name} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-serif text-4xl text-ink font-light">My Selection</h1>
            <p className="text-ink/60 text-sm mt-1">{selected.length} photo{selected.length !== 1 ? "s" : ""} chosen for your album</p>
          </div>
          <StatusBadge status={g.event.selection_status} />
        </div>

        {selected.length === 0 ? (
          <EmptyState icon={Heart} title="No photos selected yet"
            desc="Find yourself with a selfie or browse the gallery, then tap Select on the photos you love."
            action={<Link to={`/event/${eventId}/find`} className="inline-flex items-center gap-2 bg-gold text-black rounded-full px-6 py-2.5 font-medium"><ScanFace className="w-4 h-4" /> Find My Photos</Link>} />
        ) : (
          <PhotoMasonry photos={selected} onOpen={setIdx} selectable favoritable watermark={g.event.watermark} onToggleSelect={g.toggleSelect} onToggleFavorite={g.toggleFavorite} />
        )}
      </div>

      <Lightbox photos={selected} index={idx} onClose={() => setIdx(null)} setIndex={setIdx} selectable favoritable downloadable={g.event.download_permission} watermark={g.event.watermark} onToggleSelect={g.toggleSelect} onToggleFavorite={g.toggleFavorite} onDownload={setLeadPhoto} />
      <LeadModal photo={leadPhoto} eventId={eventId} onClose={() => setLeadPhoto(null)} />
      <SubmitBar count={selected.length} onSubmit={() => setConfirm(true)} />
      <SubmitConfirm open={confirm} count={selected.length} submitting={g.submitting} onCancel={() => setConfirm(false)} onConfirm={doSubmit} />
      <SubmitSuccess open={success > 0} count={success} onClose={() => setSuccess(0)} />
    </div>
  );
}
